import { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyle';
import { TextAlignment } from '../../../enums';
import { isNewline } from '../textMetricsHelpers';
import type { CanvasWordMetrics } from './CanvasWordMetrics';
import { CanvasTextLineMetrics } from './CanvasTextLineMetrics';
import {
    assignGloballyUniqueIndex,
    CanvasTextMetrics,
    positionLinesBelowEachOther,
    setAlignmentAndReadingDirection
} from './CanvasTextMetrics';

type CanvasEvenlyLayoutWordMetricsSettings = {
    wordMetricsList: CanvasWordMetrics[];
    style: CanvasTextStyleRenderer;
    alignment: TextAlignment;
    wordWrap: boolean;
    wordWrapWidth: number;
    maxHeight: number;
    removeTrailingPunctuationOnLastLineFromLastChunk: boolean;
};

/**
 * Arranges the words into lines, aiming to have the lines as evenly balanced or distributed in length as feasible. It's
 * possible for the last chunk to have one less line than the other chunks.
 */
export class CanvasEvenlyLayoutWordMetrics {
    #wordMetricsList: CanvasWordMetrics[];
    #style: CanvasTextStyleRenderer = new CanvasTextStyleRenderer();
    #alignment = TextAlignment.LEFT;
    #wordWrap = true;
    #wordWrapWidth = 400;
    #maxHeight = 400;
    #removeTrailingPunctuationOnLastLineFromLastChunk = true;

    #lines: CanvasTextLineMetrics[] = [];
    #chunks: CanvasTextMetrics[] = [];

    private constructor(settings: CanvasEvenlyLayoutWordMetricsSettings) {
        this.#wordMetricsList = settings.wordMetricsList;
        this.#style = settings.style;
        this.#alignment = settings.alignment;
        this.#wordWrap = settings.wordWrap;
        this.#wordWrapWidth = settings.wordWrapWidth;
        this.#maxHeight = settings.maxHeight;
        this.#removeTrailingPunctuationOnLastLineFromLastChunk = settings.removeTrailingPunctuationOnLastLineFromLastChunk;
    }

    #run() {
        this.#createLines();
        this.#createChunks();

        if (this.#removeTrailingPunctuationOnLastLineFromLastChunk && this.chunks.length !== 0) {
            this.chunks.at(-1).removeTrailingPunctuations();
        }
    }

    get #baselineOffset() {
        return this.#style.baselineOffset;
    }

    #createLines() {
        this.#wordMetricsList.forEach(word => word.clearCachedValues());

        if (this.#baselineOffset > this.#maxHeight) {
            console.log(`text it too tall to fit in the bounds: ${this.#baselineOffset} > ${this.#maxHeight}`);
            return;
        }

        this.#createNewLine();
        this.#wordMetricsList.forEach(wordMetrics => {
            if (isNewline(wordMetrics.text)) {
                this.#currentLine.add(wordMetrics);
                this.#createNewLine();
            }
            else {
                const futureLineWidth = wordMetrics.width + this.#currentLine.width;
                if (this.#wordWrap && futureLineWidth > this.#wordWrapWidth) {
                    this.#createNewLine();
                }
                this.#currentLine.add(wordMetrics);
            }
        });
    }

    get #currentLine() {
        return this.#lines.at(-1);
    }

    #createNewLine() {
        const line = new CanvasTextLineMetrics();
        this.#lines.push(line);
        return line;
    }

    #maxLinesPerChunk() {
        return Math.floor(this.#maxHeight / this.#baselineOffset);
    }

    #linesPerChunk() {
        let maxLinesPerChunk = this.#maxLinesPerChunk();
        while (
            // evenly divide the lines into chunks
            this.#lines.length % maxLinesPerChunk !== 0
            // one line difference is allowed
            && maxLinesPerChunk - (this.#lines.length % maxLinesPerChunk) !== 1
            // we should have at least 1 line
            && maxLinesPerChunk > 1
        ) {
            maxLinesPerChunk--;
        }
        return maxLinesPerChunk;
    }

    #numberOfChunks() {
        return Math.ceil(this.#lines.length / this.#linesPerChunk());
    }

    #createChunks() {
        const numberOfChunks = this.#numberOfChunks();
        const linesPerChunk = this.#linesPerChunk();
        for (let i = 0; i < numberOfChunks; i++) {
            const lines = this.#lines.slice(i * linesPerChunk, (i + 1) * linesPerChunk);

            lines.forEach(line => line.removeLeadingTrailingSpaces(this.#alignment));

            const lineWordMetrics = lines.flatMap(line => line.words);
            assignGloballyUniqueIndex(lineWordMetrics);

            positionLinesBelowEachOther(lines, this.#style);
            setAlignmentAndReadingDirection(lines, this.#alignment);

            const canvasTextMetrics = new CanvasTextMetrics(lines);
            this.#chunks.push(canvasTextMetrics);
        }
    }

    get chunks() {
        return this.#chunks;
    }

    static layout({
        alignment = TextAlignment.LEFT,
        wordMetricsList = [],
        style,
        wordWrap = true,
        wordWrapWidth = 400,
        maxHeight = 400,
        removeTrailingPunctuationOnLastLineFromLastChunk = true,
    }: {
        alignment?: TextAlignment;
        wordMetricsList?: CanvasWordMetrics[];
        style: CanvasTextStyleRenderer;
        wordWrap?: boolean;
        wordWrapWidth?: number;
        maxHeight?: number;
        removeTrailingPunctuationOnLastLineFromLastChunk?: boolean;
    }) {
        const layout = new CanvasEvenlyLayoutWordMetrics({
            wordMetricsList,
            alignment,
            style,
            wordWrap,
            wordWrapWidth,
            maxHeight,
            removeTrailingPunctuationOnLastLineFromLastChunk,
        });
        layout.#run();

        return layout.chunks;
    }
}
